/**
 * DOM side of the guided tours: a target counts when it is in the document AND painted (a `hidden`
 * sub-list or a rail-folded pin has no client rect, so a step pointing at it is dropped or waited for).
 * A selector may match several elements — the same anchor on a desktop block and its mobile
 * counterpart (`page-save` on the head's right slot and on the mobile bar): the first painted one wins.
 */
const isPainted = (el) => !!el && el.getClientRects().length > 0

export const findTarget = (selector) => {
    if (typeof selector !== 'string') return isPainted(selector) ? selector : null
    return Array.from(document.querySelectorAll(selector)).find(isPainted) || null
}

export const isTargetVisible = (selector) => !!findTarget(selector)

/** Resolves once `selector` is visible, or with null after `timeout` ms (hub still loading, say). */
export const waitForTarget = (selector, timeout = 3000) =>
    new Promise((resolve) => {
        const found = findTarget(selector)
        if (found) {
            resolve(found)
            return
        }
        let timer = 0
        const observer = new MutationObserver(() => {
            const el = findTarget(selector)
            if (!el) return
            clearTimeout(timer)
            observer.disconnect()
            resolve(el)
        })
        observer.observe(document.body, { childList: true, subtree: true, attributes: true })
        timer = setTimeout(() => {
            observer.disconnect()
            resolve(null)
        }, timeout)
    })

// room a bubble under the target needs (≈ 180-px bubble + gap + margins), plus one line per entry
const BUBBLE_ROOM = 220
const ENTRY_ROOM = 26
const headHeight = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--org-head-h')) || 80

/**
 * The target of a step that shows a whole block (a section card, a hub tab, a list): the block is first
 * scrolled under the sticky head (antd only scrolls a target that is fully out of view, and a band
 * already on screen would leave the body below the fold). Then the whole block when it fits with a
 * bubble below it, else its `band` (a selector inside the block: the title band, a table head, the
 * first row) — a spotlight taller than the screen leaves the bubble off-screen, while the body stays
 * visible under the bubble (William, 29 Oct 2026: « montrer la section en entier, pas seulement le titre »).
 */
export const blockTarget = (selector, band = SECTION_BAND, entries = 0) => {
    const block = findTarget(selector)
    if (!block) return null
    scrollUnderHead(block)
    const room = window.innerHeight - headHeight() - 12 - BUBBLE_ROOM - ENTRY_ROOM * entries
    if (block.getBoundingClientRect().height <= room) return block
    return (band && block.querySelector(band)) || block
}
export const SECTION_BAND = ':scope > .org-sec-h'
export const sectionTarget = (selector) => blockTarget(selector, SECTION_BAND)

/** Scrolls the window so `el` sits right under the sticky head (instant; no-op when already there). */
export const scrollUnderHead = (el) => {
    if (!el) return
    const delta = el.getBoundingClientRect().top - (headHeight() + 12)
    if (Math.abs(delta) > 2) window.scrollBy({ top: delta, left: 0, behavior: 'auto' })
}

/**
 * Puts a step's target where antd will measure it, BEFORE the step shows: a block under the head
 * (`section` / `block`), or any target whose bubble is tall (`scroll: 'start'`, a select with a list of
 * entries). Scrolling inside antd's own measure left the bubble aligned on a stale rect (28 Oct 2026).
 */
export const prepareTarget = (step) => {
    if (!step?.target) return
    if (step.section || step.block) blockTarget(step.target, bandOf(step), step.entries?.length || 0)
    else if (step.scroll === 'start') scrollUnderHead(findTarget(step.target))
}

/** The band selector of a block step: its own `block`, or the section card's title band. */
export const bandOf = (step) => (typeof step?.block === 'string' && step.block) || SECTION_BAND

/** An antd modal or one of the kit's drawers / side panels is open: not the moment for a tour. */
export const isDialogOpen = () =>
    !!document.querySelector('[aria-modal="true"]') ||
    Array.from(document.querySelectorAll('.ant-modal-wrap')).some((wrap) => wrap.style.display !== 'none')
